import { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { Plus, Edit, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';

export default function BankManagement() {
  const { currentUser } = useApp();
  const [bankalar, setBankalar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ ad: '', tur: '', aktifMi: true });
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ ad: '', tur: 'kartPos', aktifMi: true });

  useEffect(() => {
    loadBankalar();
  }, []);

  const loadBankalar = async () => {
    try {
      const res = await api.getBankalar();
      setBankalar(res.data);
    } catch (error) {
      console.error('Error loading banks:', error);
      toast.error('Bankalar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (banka) => {
    setEditingId(banka.id);
    setEditForm({ ad: banka.ad, tur: banka.tur, aktifMi: banka.aktifMi });
  };

  const handleSaveEdit = async () => {
    try {
      await api.updateBanka(editingId, editForm);
      toast.success('Banka güncellendi');
      setEditingId(null);
      loadBankalar();
    } catch (error) {
      console.error('Error updating bank:', error);
      toast.error('Banka güncellenirken hata oluştu');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleAdd = async () => {
    if (!addForm.ad) {
      toast.error('Lütfen banka adı girin');
      return;
    }

    try {
      await api.createBanka(addForm);
      toast.success('Banka eklendi');
      setShowAddForm(false);
      setAddForm({ ad: '', tur: 'kartPos', aktifMi: true });
      loadBankalar();
    } catch (error) {
      console.error('Error adding bank:', error);
      toast.error('Banka eklenirken hata oluştu');
    }
  };

  if (currentUser?.rol !== 'admin') {
    return (
      <div className="text-center py-12">
        <p className="text-textSecondary">Bu sayfaya erişim yetkiniz yok</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-textSecondary">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-8" data-testid="bank-management">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-xl md:text-3xl font-heading font-bold text-textPrimary">Banka Yönetimi</h1>
          <p className="text-xs md:text-base text-textSecondary mt-1 md:mt-2">Banka hesaplarını yönetin</p>
        </div>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          data-testid="toggle-add-form-btn"
          className="bg-primary hover:bg-primaryHover text-primaryForeground"
        >
          <Plus className="h-5 w-5 mr-2" />
          Yeni Banka Ekle
        </Button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-surface border border-border p-4 md:p-6" data-testid="add-form">
          <h2 className="text-base md:text-lg font-heading font-bold text-textPrimary mb-3 md:mb-4">Yeni Banka Ekle</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Banka Adı</label>
              <Input
                data-testid="add-banka-ad-input"
                value={addForm.ad}
                onChange={(e) => setAddForm({ ...addForm, ad: e.target.value })}
                placeholder="Örn: AKBANK"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Tür</label>
              <Select
                value={addForm.tur}
                onValueChange={(value) => setAddForm({ ...addForm, tur: value })}
              >
                <SelectTrigger data-testid="add-tur-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="kartPos">Kart POS</SelectItem>
                  <SelectItem value="havale">Havale</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button
                onClick={handleAdd}
                data-testid="save-add-btn"
                className="bg-success hover:bg-success/90 text-white"
              >
                <Check className="h-4 w-4 mr-2" />
                Kaydet
              </Button>
              <Button
                onClick={() => setShowAddForm(false)}
                data-testid="cancel-add-btn"
                variant="outline"
              >
                İptal
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Banks Table */}
      <div className="bg-surface border border-border">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="bankalar-table">
            <thead className="bg-surfaceSubtle border-b border-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Banka Adı</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Tür</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Durum</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {bankalar.map((banka, index) => (
                <tr key={banka.id} data-testid={`banka-row-${index}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingId === banka.id ? (
                      <Input
                        data-testid={`edit-ad-input-${index}`}
                        value={editForm.ad}
                        onChange={(e) => setEditForm({ ...editForm, ad: e.target.value })}
                        className="max-w-xs"
                      />
                    ) : (
                      <span className="text-sm font-medium text-textPrimary">{banka.ad}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingId === banka.id ? (
                      <Select
                        value={editForm.tur}
                        onValueChange={(value) => setEditForm({ ...editForm, tur: value })}
                      >
                        <SelectTrigger data-testid={`edit-tur-select-${index}`} className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="kartPos">Kart POS</SelectItem>
                          <SelectItem value="havale">Havale</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-sm text-textPrimary">
                        {banka.tur === 'kartPos' ? 'Kart POS' : 'Havale'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingId === banka.id ? (
                      <Select
                        value={editForm.aktifMi ? 'aktif' : 'pasif'}
                        onValueChange={(value) => setEditForm({ ...editForm, aktifMi: value === 'aktif' })}
                      >
                        <SelectTrigger data-testid={`edit-aktif-select-${index}`} className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="aktif">Aktif</SelectItem>
                          <SelectItem value="pasif">Pasif</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className={`px-2 py-1 text-xs font-medium ${
                        banka.aktifMi ? 'bg-success text-white' : 'bg-error text-white'
                      }`}>
                        {banka.aktifMi ? 'Aktif' : 'Pasif'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {editingId === banka.id ? (
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveEdit}
                          data-testid={`save-edit-btn-${index}`}
                          className="text-success hover:text-success/80"
                        >
                          <Check className="h-5 w-5" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          data-testid={`cancel-edit-btn-${index}`}
                          className="text-error hover:text-error/80"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleEdit(banka)}
                        data-testid={`edit-btn-${index}`}
                        className="text-primary hover:text-primaryHover font-medium"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}